import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  IoMapOutline,
  IoTimeOutline,
  IoPersonOutline,
  IoCheckmarkCircle,
  IoEllipseOutline,
  IoPlayCircleOutline,
  IoNavigateOutline,
  IoCalendarOutline,
  IoChevronForward,
  IoAddCircleOutline,
  IoLocationOutline,
  IoSparklesOutline,
  IoDocumentTextOutline,
  IoPencilOutline,
  IoCloseOutline,
  IoCheckmarkOutline,
  IoReorderThreeOutline,
  IoCarOutline,
  IoAlertCircleOutline,
  IoRefreshOutline,
} from 'react-icons/io5'
import { useAppointments, useUpdateAppointment } from '../hooks/useAppointments'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { api } from '../services/api/client'
import toast from 'react-hot-toast'
import type { Appointment } from '../types'

/* ─── Types ─── */
type VisitStatus = 'pending' | 'in_progress' | 'done' | 'skipped'

interface GeoCoords { lat: number; lng: number }
interface VisitWithCoords extends Appointment { coords?: GeoCoords; distance?: number; eta?: string }

/* ─── Helpers ─── */
const toApiStatus = (s: VisitStatus): string => {
  if (s === 'in_progress') return 'IN_PROGRESS'
  if (s === 'done') return 'COMPLETED'
  if (s === 'skipped') return 'SKIPPED'
  return 'PENDING'
}
const fromApiStatus = (status?: string): VisitStatus => {
  if (status === 'IN_PROGRESS') return 'in_progress'
  if (status === 'COMPLETED') return 'done'
  if (status === 'SKIPPED') return 'skipped'
  return 'pending'
}
const formatTime = (date: string) =>
  new Date(date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
const formatDuration = (start: string, end: string) => {
  const diff = new Date(end).getTime() - new Date(start).getTime()
  const mins = Math.round(diff / 60000)
  if (mins < 60) return `${mins} min`
  return `${Math.floor(mins / 60)}h${mins % 60 > 0 ? String(mins % 60).padStart(2, '0') : ''}`
}
const getInitials = (first?: string, last?: string) => {
  if (!first && !last) return '?'
  return `${(first ?? '')[0] ?? ''}${(last ?? '')[0] ?? ''}`.toUpperCase()
}
function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6371, dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1)
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}
const toRad = (d: number) => d * Math.PI / 180

/* ─── Geocoding (Nominatim, gratuit et sans clé API) ─── */
async function geocodeAddress(address: string): Promise<GeoCoords | null> {
  try {
    const q = encodeURIComponent(address)
    const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`, {
      headers: { 'Accept-Language': 'fr' },
    })
    const data = await res.json() as Array<{ lat: string; lon: string }>
    if (data.length === 0) return null
    return { lat: parseFloat(data[0]!.lat), lng: parseFloat(data[0]!.lon) }
  } catch { return null }
}

/* ─── Canvas Signature ─── */
function SignatureCanvas({ onSave, onClose }: { onSave: (dataUrl: string) => void; onClose: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const [hasStrokes, setHasStrokes] = useState(false)

  const getPos = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect()
    const src = 'touches' in e ? e.touches[0]! : e
    return { x: src.clientX - rect.left, y: src.clientY - rect.top }
  }

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    const canvas = canvasRef.current; if (!canvas) return
    const ctx = canvas.getContext('2d')!
    const pos = getPos(e, canvas)
    ctx.beginPath(); ctx.moveTo(pos.x, pos.y)
    drawing.current = true
  }
  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    if (!drawing.current) return
    const canvas = canvasRef.current; if (!canvas) return
    const ctx = canvas.getContext('2d')!
    const pos = getPos(e, canvas)
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#1e293b'
    ctx.lineTo(pos.x, pos.y); ctx.stroke()
    setHasStrokes(true)
  }
  const stopDraw = () => { drawing.current = false }
  const clear = () => {
    const canvas = canvasRef.current; if (!canvas) return
    canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height)
    setHasStrokes(false)
  }
  const save = () => {
    const canvas = canvasRef.current; if (!canvas) return
    onSave(canvas.toDataURL('image/png'))
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className='fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4'
      onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        className='bg-white dark:bg-dark-50 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md'>
        <div className='flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800'>
          <div className='flex items-center gap-2'>
            <IoPencilOutline className='text-primary-500 text-lg' />
            <h3 className='font-bold text-gray-900 dark:text-white'>Signature du patient</h3>
          </div>
          <button onClick={onClose} className='p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'>
            <IoCloseOutline className='text-gray-500 text-xl' />
          </button>
        </div>
        <div className='p-4'>
          <p className='text-xs text-gray-500 dark:text-gray-400 mb-3 text-center'>
            Le patient signe ci-dessous pour valider la visite
          </p>
          <div className='border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl overflow-hidden bg-gray-50 dark:bg-dark-100'>
            <canvas
              ref={canvasRef}
              width={400} height={180}
              className='w-full touch-none cursor-crosshair block'
              style={{ maxHeight: 180 }}
              onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
              onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
            />
          </div>
          <p className='text-xs text-gray-400 text-center mt-2'>Signez dans la zone ci-dessus</p>
        </div>
        <div className='flex gap-2 p-4 border-t border-gray-100 dark:border-gray-800'>
          <button onClick={clear} className='flex-1 btn-secondary text-sm py-2'>
            Effacer
          </button>
          <button onClick={save} disabled={!hasStrokes}
            className='flex-1 bg-primary-500 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold py-2 px-4 rounded-xl transition-colors flex items-center justify-center gap-2'>
            <IoCheckmarkOutline /> Valider
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

/* ─── Config statut ─── */
const statusConfig: Record<VisitStatus, { label: string; color: string; bg: string; border: string; dot: string; icon: React.ElementType }> = {
  pending: { label: 'En attente', color: 'text-gray-500 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-gray-800', border: 'border-gray-200 dark:border-gray-700', dot: 'bg-gray-400', icon: IoEllipseOutline },
  in_progress: { label: 'En cours', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-500/10', border: 'border-blue-200 dark:border-blue-500/30', dot: 'bg-blue-500', icon: IoPlayCircleOutline },
  done: { label: 'Terminé', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-500/10', border: 'border-emerald-200 dark:border-emerald-500/30', dot: 'bg-emerald-500', icon: IoCheckmarkCircle },
  skipped: { label: 'Passé', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-500/10', border: 'border-amber-200 dark:border-amber-500/30', dot: 'bg-amber-400', icon: IoAlertCircleOutline },
}

/* ─── Mini carte statique via OpenStreetMap ─── */
function MiniMap({ visits, userCoords }: { visits: VisitWithCoords[]; userCoords?: GeoCoords | null }) {
  const withCoords = visits.filter(v => v.coords)
  if (withCoords.length === 0 && !userCoords) return null

  const allPoints = [...(userCoords ? [userCoords] : []), ...withCoords.map(v => v.coords!)]
  const minLat = Math.min(...allPoints.map(p => p.lat)) - 0.01
  const maxLat = Math.max(...allPoints.map(p => p.lat)) + 0.01
  const minLng = Math.min(...allPoints.map(p => p.lng)) - 0.01
  const maxLng = Math.max(...allPoints.map(p => p.lng)) + 0.01

  const bbox = `${minLng},${minLat},${maxLng},${maxLat}`
  const markers = [
    ...(userCoords ? [`${userCoords.lat},${userCoords.lng},lightblue`] : []),
    ...withCoords.map((v, i) => {
      const s = fromApiStatus(v.status)
      const color = s === 'done' ? 'green' : s === 'in_progress' ? 'blue' : s === 'skipped' ? 'orange' : 'red'
      return `${v.coords!.lat},${v.coords!.lng},${color}${i + 1}`
    }),
  ].join('|')

  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${markers}`

  return (
    <div className='rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm'>
      <div className='flex items-center gap-2 px-4 py-2 bg-white dark:bg-dark-50 border-b border-gray-100 dark:border-gray-800'>
        <IoMapOutline className='text-primary-500 text-sm' />
        <span className='text-xs font-semibold text-gray-600 dark:text-gray-300'>Carte de la tournée</span>
        {userCoords && <span className='ml-auto text-xs text-emerald-500 flex items-center gap-1'><span className='w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block' />Ma position</span>}
      </div>
      <iframe
        src={src}
        title='Carte tournée'
        className='w-full h-52 border-0'
        loading='lazy'
      />
    </div>
  )
}

/* ─── Génération PDF via impression navigateur ─── */
function generateTourneeReport(visits: VisitWithCoords[], doneCount: number, nurseInfo?: string) {
  const dateStr = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  const rows = visits.map((v, i) => {
    const s = fromApiStatus(v.status)
    const sLabel = statusConfig[s].label
    const sColor = s === 'done' ? '#10b981' : s === 'in_progress' ? '#3b82f6' : s === 'skipped' ? '#f59e0b' : '#6b7280'
    const duration = formatDuration(v.startDate, v.endDate)
    const sigImg = v.patientSignature ? `<img src="${v.patientSignature}" style="height:40px;border:1px solid #e2e8f0;border-radius:6px;padding:2px;background:#fff"/>` : '<span style="color:#9ca3af;font-style:italic;font-size:11px">Non signée</span>'
    return `
      <tr style="border-bottom:1px solid #f1f5f9;">
        <td style="padding:10px 8px;font-weight:600;color:#64748b">${i + 1}</td>
        <td style="padding:10px 8px">
          <div style="font-weight:700;color:#0f172a">${v.patient ? `${v.patient.lastName.toUpperCase()} ${v.patient.firstName}` : '—'}</div>
          ${v.patient?.disease ? `<div style="font-size:11px;color:#64748b;margin-top:2px">${v.patient.disease}</div>` : ''}
          ${v.patient?.adresse ? `<div style="font-size:11px;color:#94a3b8;margin-top:1px">📍 ${v.patient.adresse}</div>` : ''}
        </td>
        <td style="padding:10px 8px;color:#475569">${formatTime(v.startDate)} – ${formatTime(v.endDate)}<br/><span style="font-size:11px;color:#94a3b8">${duration}</span></td>
        <td style="padding:10px 8px"><span style="color:${sColor};font-weight:600;font-size:12px">${sLabel}</span></td>
        <td style="padding:10px 8px">${sigImg}</td>
        <td style="padding:10px 8px;font-size:11px;color:#64748b;font-style:italic">${v.notes ?? ''}</td>
      </tr>`
  }).join('')

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
  <title>Rapport de tournée — ${dateStr}</title>
  <style>
    body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#0f172a;margin:0;padding:32px;background:#fff}
    h1{font-size:22px;font-weight:800;color:#0f172a;margin:0 0 4px}
    .meta{font-size:13px;color:#64748b;margin-bottom:24px}
    .stats{display:flex;gap:12px;margin-bottom:24px}
    .stat{background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:12px 18px;text-align:center}
    .stat-value{font-size:24px;font-weight:800;color:#0f172a}
    .stat-label{font-size:11px;color:#64748b;margin-top:2px}
    table{width:100%;border-collapse:collapse;font-size:13px}
    th{background:#f8fafc;color:#475569;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;padding:10px 8px;text-align:left;border-bottom:2px solid #e2e8f0}
    tr:hover{background:#fafafe}
    @media print{body{padding:20px}button{display:none}}
  </style></head><body>
  <div style="display:flex;align-items:center;gap:12px;margin-bottom:6px">
    <div style="width:40px;height:40px;background:#6366f1;border-radius:10px;display:flex;align-items:center;justify-content:center;color:white;font-size:20px">🏥</div>
    <div><h1>Rapport de tournée</h1><div class="meta">${dateStr}${nurseInfo ? ` · ${nurseInfo}` : ''}</div></div>
  </div>
  <div class="stats">
    <div class="stat"><div class="stat-value">${visits.length}</div><div class="stat-label">Total visites</div></div>
    <div class="stat"><div class="stat-value" style="color:#10b981">${doneCount}</div><div class="stat-label">Terminées</div></div>
    <div class="stat"><div class="stat-value" style="color:#f59e0b">${visits.filter(v => fromApiStatus(v.status) === 'skipped').length}</div><div class="stat-label">Passées</div></div>
    <div class="stat"><div class="stat-value" style="color:#6366f1">${Math.round(doneCount / Math.max(visits.length, 1) * 100)}%</div><div class="stat-label">Taux de complétion</div></div>
  </div>
  <table>
    <thead><tr><th>#</th><th>Patient</th><th>Horaire</th><th>Statut</th><th>Signature</th><th>Notes</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div style="margin-top:32px;padding-top:16px;border-top:1px solid #e2e8f0;font-size:11px;color:#94a3b8;text-align:center">
    Rapport généré par MediSync · ${new Date().toLocaleString('fr-FR')}
  </div>
  <script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
  </body></html>`

  const w = window.open('', '_blank')
  if (w) { w.document.write(html); w.document.close() }
  else toast.error('Autorisez les popups pour générer le PDF')
}

/* ═══════════════════════════════════════════════════
   COMPOSANT PRINCIPAL
═══════════════════════════════════════════════════ */
export default function Tournee() {
  const navigate = useNavigate()
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d }, [])

  const [selectedVisit, setSelectedVisit] = useState<Appointment | null>(null)
  const [showSignature, setShowSignature] = useState<number | null>(null)
  const [userCoords, setUserCoords] = useState<GeoCoords | null>(null)
  const [geoStatus, setGeoStatus] = useState<'idle' | 'loading' | 'ok' | 'denied'>('idle')
  const [geocodedVisits, setGeocodedVisits] = useState<VisitWithCoords[]>([])
  const [isOptimizing, setIsOptimizing] = useState(false)
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [view, setView] = useState<'list' | 'map'>('list')
  const updateAppointment = useUpdateAppointment()

  const todayStart = new Date(today); todayStart.setHours(0, 0, 0, 0)
  const todayEnd = new Date(today); todayEnd.setHours(23, 59, 59, 999)

  const { data: appointmentsData, isLoading, refetch } = useAppointments({
    limit: 200, sortOrder: 'ASC',
    startDate: todayStart.toISOString(),
    endDate: todayEnd.toISOString(),
  })

  const appointments = appointmentsData?.data ?? []
  const todayVisits = useMemo(() => {
    const pad = (n: number) => String(n).padStart(2, '0')
    const key = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`
    return appointments.filter(a => {
      const d = new Date(a.startDate)
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` === key
    })
  }, [appointments, today])

  // Synchronise geocodedVisits quand todayVisits change
  useEffect(() => {
    setGeocodedVisits(todayVisits.map(v => ({ ...v })))
  }, [todayVisits])

  // Géolocalisation de l'infirmier
  const startGeolocation = useCallback(() => {
    if (!navigator.geolocation) { setGeoStatus('denied'); return }
    setGeoStatus('loading')
    navigator.geolocation.watchPosition(
      pos => {
        setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude })
        setGeoStatus('ok')
      },
      () => setGeoStatus('denied'),
      { enableHighAccuracy: true, maximumAge: 10000 }
    )
  }, [])

  // Géocodage des adresses patients
  const userCoordsRef = useRef(userCoords)
  useEffect(() => { userCoordsRef.current = userCoords }, [userCoords])
  const geocodedVisitsRef = useRef(geocodedVisits)
  useEffect(() => { geocodedVisitsRef.current = geocodedVisits }, [geocodedVisits])

  const geocodeAll = useCallback(async () => {
    const current = geocodedVisitsRef.current
    if (current.length === 0) return
    setIsGeocoding(true)
    const results = await Promise.all(
      current.map(async v => {
        if (v.coords) return v
        const addr = v.patient?.adresse ?? v.patient?.additionalInfo
        if (!addr) return v
        const coords = await geocodeAddress(addr)
        return { ...v, coords: coords ?? undefined }
      })
    )
    const cur = userCoordsRef.current
    const withDist = results.map(v => {
      if (!v.coords || !cur) return v
      const km = haversineKm(cur.lat, cur.lng, v.coords.lat, v.coords.lng)
      const etaMin = Math.round(km / 30 * 60)
      return { ...v, distance: Math.round(km * 10) / 10, eta: etaMin < 60 ? `${etaMin} min` : `${Math.floor(etaMin / 60)}h${etaMin % 60}` }
    })
    setGeocodedVisits(withDist)
    setIsGeocoding(false)
    toast.success('Adresses géocodées')
  }, [])

  // Optimisation ordre
  const optimizeOrder = useCallback(async () => {
    const current = geocodedVisitsRef.current
    const toOptimize = current.map(v => ({
      id: v.id,
      lat: v.coords?.lat,
      lng: v.coords?.lng,
      address: v.patient?.adresse,
    }))
    setIsOptimizing(true)
    try {
      const res = await api.post('/dashboard/optimize-tour', { visits: toOptimize })
      const optimized = (res.data as { data: Array<{ id: number }> }).data
      const idOrder = optimized.map(o => o.id)
      const reordered = idOrder.map(id => current.find(v => v.id === id)).filter(Boolean) as VisitWithCoords[]
      setGeocodedVisits(reordered)

      await Promise.all(reordered.map((v) =>
        updateAppointment.mutateAsync({ id: v.id, data: { status: v.status as string | undefined } })
          .catch(() => null)
      ))
      toast.success('Tournée optimisée !')
    } catch { toast.error("Erreur lors de l'optimisation") }
    finally { setIsOptimizing(false) }
  }, [updateAppointment])

  const setStatus = useCallback((id: number, status: VisitStatus) => {
    void updateAppointment.mutateAsync({ id, data: { status: toApiStatus(status) } })
      .then(() => refetch())
  }, [updateAppointment, refetch])

  const saveSignature = useCallback(async (visitId: number, dataUrl: string) => {
    await updateAppointment.mutateAsync({
      id: visitId,
      data: { patientSignature: dataUrl, status: 'COMPLETED' },
    })
    setShowSignature(null)
    setSelectedVisit(null)
    toast.success('Signature enregistrée, visite validée ✓')
    void refetch()
  }, [updateAppointment, refetch])

  const visitsToDisplay = geocodedVisits.length > 0 ? geocodedVisits : todayVisits
  const doneCount = visitsToDisplay.filter(v => fromApiStatus(v.status) === 'done').length
  const inProgressCount = visitsToDisplay.filter(v => fromApiStatus(v.status) === 'in_progress').length
  const pendingCount = visitsToDisplay.filter(v => fromApiStatus(v.status) === 'pending').length
  const progress = visitsToDisplay.length > 0 ? (doneCount / visitsToDisplay.length) * 100 : 0
  const dateLabel = today.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
  const signatureVisit = showSignature ? visitsToDisplay.find(v => v.id === showSignature) : null

  return (
    <div className='min-h-full bg-gray-50 dark:bg-dark-200'>
      {/* Signature modal */}
      <AnimatePresence>
        {showSignature && signatureVisit && (
          <SignatureCanvas
            onSave={dataUrl => void saveSignature(showSignature, dataUrl)}
            onClose={() => setShowSignature(null)}
          />
        )}
      </AnimatePresence>

      <div className='max-w-3xl mx-auto p-4 lg:p-6 space-y-4'>

        {/* ── Header ── */}
        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }}
          className='flex items-start justify-between gap-3'>
          <div className='flex items-center gap-3'>
            <div className='bg-primary-500/10 p-2.5 rounded-xl'>
              <IoMapOutline className='text-primary-500 text-2xl' />
            </div>
            <div>
              <h1 className='text-xl font-bold text-gray-900 dark:text-white'>Tournée du jour</h1>
              <p className='text-sm text-gray-500 dark:text-gray-400 capitalize'>{dateLabel}</p>
            </div>
          </div>
          <div className='flex items-center gap-2 flex-wrap justify-end'>
            {/* Géolocalisation */}
            <button
              onClick={geoStatus === 'idle' ? startGeolocation : undefined}
              title={geoStatus === 'ok' ? 'Position active' : geoStatus === 'denied' ? 'Accès refusé' : 'Activer la géolocalisation'}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border transition-all ${
                geoStatus === 'ok' ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30 text-emerald-600' :
                geoStatus === 'denied' ? 'bg-red-50 dark:bg-red-500/10 border-red-200 text-red-500' :
                'bg-white dark:bg-dark-50 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-primary-300'
              }`}
            >
              {geoStatus === 'loading' ? <LoadingSpinner size='sm' /> :
                <IoLocationOutline className={`text-base ${geoStatus === 'ok' ? 'text-emerald-500' : ''}`} />}
              <span className='hidden sm:inline'>
                {geoStatus === 'ok' ? 'GPS actif' : geoStatus === 'denied' ? 'Refusé' : 'Ma position'}
              </span>
              {geoStatus === 'ok' && <span className='w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse' />}
            </button>

            {/* Export PDF */}
            <button
              onClick={() => generateTourneeReport(visitsToDisplay as VisitWithCoords[], doneCount)}
              className='flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-white dark:bg-dark-50 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-primary-300 transition-all'
            >
              <IoDocumentTextOutline className='text-base' />
              <span className='hidden sm:inline'>PDF</span>
            </button>

            {/* Toggle vue */}
            <div className='flex bg-white dark:bg-dark-50 rounded-xl border border-gray-200 dark:border-gray-700 p-0.5'>
              {(['list', 'map'] as const).map(v => (
                <button key={v} onClick={() => setView(v)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    view === v ? 'bg-primary-500 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                  }`}>
                  {v === 'list' ? <IoReorderThreeOutline className='text-base' /> : <IoMapOutline className='text-base' />}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 }}
          className='flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100'>
          <IoSparklesOutline className='mt-0.5 shrink-0 text-lg text-emerald-600 dark:text-emerald-400' />
          <div>
            <div className='flex items-center gap-2 flex-wrap'>
              <p className='text-sm font-bold'>Optimisation des tournées</p>
              <span className='rounded-full bg-emerald-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200'>
                Version bêta
              </span>
            </div>
            <p className='mt-1 text-xs leading-relaxed text-emerald-800/80 dark:text-emerald-100/70'>
              La version actuelle propose un ordre indicatif basé sur les distances géographiques. Les créneaux de rendez-vous, les priorités médicales, les urgences et le trafic seront intégrés dans une prochaine version.
            </p>
          </div>
        </motion.div>

        {/* ── Barre de progression ── */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className='bg-white dark:bg-dark-50 rounded-2xl p-4 border border-gray-100 dark:border-gray-800 shadow-sm'>
          <div className='flex items-center justify-between mb-2'>
            <span className='text-sm font-semibold text-gray-700 dark:text-gray-200'>Progression</span>
            <span className='text-sm font-bold text-primary-500'>{doneCount}/{visitsToDisplay.length} visites</span>
          </div>
          <div className='w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5 overflow-hidden'>
            <motion.div className='h-full rounded-full bg-gradient-to-r from-primary-500 to-primary-400'
              initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.8, ease: 'easeOut' }} />
          </div>
          <div className='flex items-center gap-4 mt-2.5 text-xs text-gray-500 dark:text-gray-400'>
            <span className='flex items-center gap-1'><span className='w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600' />{pendingCount} en attente</span>
            <span className='flex items-center gap-1'><span className='w-2 h-2 rounded-full bg-blue-400' />{inProgressCount} en cours</span>
            <span className='flex items-center gap-1'><span className='w-2 h-2 rounded-full bg-emerald-400' />{doneCount} terminées</span>
            {visitsToDisplay.filter(v => fromApiStatus(v.status) === 'skipped').length > 0 && (
              <span className='flex items-center gap-1'><span className='w-2 h-2 rounded-full bg-amber-400' />{visitsToDisplay.filter(v => fromApiStatus(v.status) === 'skipped').length} passées</span>
            )}
          </div>
        </motion.div>

        {/* ── Actions globales (géocode + optimise) ── */}
        {visitsToDisplay.length > 1 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className='flex gap-2'>
            <button onClick={() => void geocodeAll()} disabled={isGeocoding}
              className='flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white dark:bg-dark-50 border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:border-primary-400 hover:text-primary-600 transition-all disabled:opacity-50'>
              {isGeocoding ? <LoadingSpinner size='sm' /> : <IoLocationOutline className='text-base' />}
              {isGeocoding ? 'Géocodage…' : 'Géocoder les adresses'}
            </button>
            <button onClick={() => void optimizeOrder()} disabled={isOptimizing || geocodedVisits.every(v => !v.coords)}
              className='flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm font-semibold shadow-sm hover:from-primary-600 hover:to-primary-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed'>
              {isOptimizing ? <LoadingSpinner size='sm' /> : <IoSparklesOutline className='text-base' />}
              {isOptimizing ? 'Préparation…' : 'Tester l\'optimisation'}
            </button>
          </motion.div>
        )}

        {/* ── Vue carte ── */}
        <AnimatePresence mode='wait'>
          {view === 'map' && (
            <motion.div key='map' initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <MiniMap visits={geocodedVisits} userCoords={userCoords} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Liste visites ── */}
        {isLoading ? (
          <div className='flex justify-center py-16'><LoadingSpinner size='lg' /></div>
        ) : visitsToDisplay.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className='bg-white dark:bg-dark-50 rounded-2xl p-12 border border-gray-100 dark:border-gray-800 text-center shadow-sm'>
            <IoCalendarOutline className='text-gray-300 dark:text-gray-600 text-5xl mx-auto mb-4' />
            <h3 className='text-lg font-semibold text-gray-800 dark:text-gray-100 mb-1'>Aucune visite aujourd'hui</h3>
            <p className='text-gray-400 text-sm mb-6'>Ajoutez des rendez-vous depuis le planning</p>
            <Link to='/planning' className='btn-primary inline-flex items-center gap-2'>
              <IoCalendarOutline /> Voir le planning
            </Link>
          </motion.div>
        ) : (
          <div className='space-y-2'>
            {visitsToDisplay.map((visit, index) => {
              const status = fromApiStatus(visit.status)
              const cfg = statusConfig[status]
              const StatusIcon = cfg.icon
              const gv = visit as VisitWithCoords

              return (
                <motion.div key={visit.id}
                  initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + index * 0.04 }}
                  className={`bg-white dark:bg-dark-50 rounded-2xl border ${cfg.border} shadow-sm overflow-hidden transition-all`}>

                  {/* Ligne principale */}
                  <button className='w-full text-left p-4' onClick={() => setSelectedVisit(selectedVisit?.id === visit.id ? null : visit)}>
                    <div className='flex items-center gap-3'>
                      {/* Index */}
                      <div className='flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs font-bold text-gray-500 dark:text-gray-400'>
                        {index + 1}
                      </div>
                      {/* Avatar */}
                      <div className='flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-primary-400/30 to-primary-600/30 border border-primary-400/30 flex items-center justify-center'>
                        <span className='text-xs font-bold text-primary-600 dark:text-primary-300'>
                          {getInitials(visit.patient?.firstName, visit.patient?.lastName)}
                        </span>
                      </div>
                      {/* Infos */}
                      <div className='flex-1 min-w-0'>
                        <p className='font-semibold text-gray-900 dark:text-white truncate text-sm'>
                          {visit.patient ? `${visit.patient.lastName.toUpperCase()} ${visit.patient.firstName}` : 'Patient inconnu'}
                        </p>
                        <div className='flex items-center gap-2 mt-0.5 flex-wrap'>
                          <span className='flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400'>
                            <IoTimeOutline className='text-[11px]' />
                            {formatTime(visit.startDate)} · {formatDuration(visit.startDate, visit.endDate)}
                          </span>
                          {gv.distance != null && (
                            <span className='flex items-center gap-1 text-xs text-primary-500 dark:text-primary-400'>
                              <IoCarOutline className='text-[11px]' />{gv.distance} km · {gv.eta}
                            </span>
                          )}
                          {(visit as VisitWithCoords & { patientSignature?: string }).patientSignature && (
                            <span className='flex items-center gap-1 text-xs text-emerald-500'><IoCheckmarkCircle className='text-[11px]' />Signé</span>
                          )}
                        </div>
                      </div>
                      {/* Badge statut */}
                      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${cfg.bg} border ${cfg.border} flex-shrink-0`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} ${status === 'in_progress' ? 'animate-pulse' : ''}`} />
                        <StatusIcon className={`text-sm ${cfg.color}`} />
                        <span className={`text-xs font-semibold ${cfg.color} hidden md:block`}>{cfg.label}</span>
                      </div>
                      <IoChevronForward className={`text-gray-300 dark:text-gray-600 flex-shrink-0 transition-transform ${selectedVisit?.id === visit.id ? 'rotate-90' : ''}`} />
                    </div>
                  </button>

                  {/* Panneau détail */}
                  <AnimatePresence>
                    {selectedVisit?.id === visit.id && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                        className='overflow-hidden border-t border-gray-100 dark:border-gray-800'>
                        <div className='p-4 space-y-3 bg-gray-50/50 dark:bg-dark-100/30'>

                          {/* Adresse */}
                          {visit.patient?.adresse && (
                            <div className='flex items-start gap-2 text-sm'>
                              <IoLocationOutline className='text-gray-400 mt-0.5 flex-shrink-0' />
                              <span className='text-gray-600 dark:text-gray-300'>{visit.patient.adresse}</span>
                            </div>
                          )}
                          {/* Pathologie */}
                          {visit.patient?.disease && (
                            <div className='flex items-start gap-2 text-sm'>
                              <IoPersonOutline className='text-gray-400 mt-0.5 flex-shrink-0' />
                              <span className='text-gray-600 dark:text-gray-300'>{visit.patient.disease}</span>
                            </div>
                          )}
                          {/* Notes */}
                          {visit.notes && (
                            <div className='bg-white dark:bg-dark-50 rounded-xl px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400 italic border border-gray-100 dark:border-gray-800'>
                              {visit.notes}
                            </div>
                          )}

                          {/* Signature existante */}
                          {(visit as VisitWithCoords & { patientSignature?: string }).patientSignature && (
                            <div className='flex items-center gap-3 bg-emerald-50 dark:bg-emerald-500/10 rounded-xl p-3 border border-emerald-200 dark:border-emerald-500/30'>
                              <IoCheckmarkCircle className='text-emerald-500 text-lg flex-shrink-0' />
                              <div>
                                <p className='text-xs font-semibold text-emerald-700 dark:text-emerald-400'>Visite signée par le patient</p>
                                <img
                                  src={(visit as VisitWithCoords & { patientSignature?: string }).patientSignature}
                                  alt='Signature'
                                  className='h-10 mt-1 bg-white rounded border border-emerald-200 p-1'
                                />
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className='flex flex-wrap gap-2 pt-1'>
                            <Link to={`/patients/${visit.patientId}`}
                              className='flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white dark:bg-dark-50 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-all'>
                              <IoPersonOutline className='text-base' />Dossier
                            </Link>

                            {(visit.patient?.adresse ?? visit.patient?.additionalInfo) && (
                              <button
                                onClick={() => {
                                  const addr = visit.patient?.adresse ?? visit.patient?.additionalInfo ?? ''
                                  window.open(`https://maps.google.com/?q=${encodeURIComponent(addr)}`, '_blank')
                                }}
                                className='flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white dark:bg-dark-50 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:border-amber-400 hover:text-amber-600 transition-all'>
                                <IoNavigateOutline className='text-base' />Itinéraire
                              </button>
                            )}

                            {status === 'pending' && (
                              <button onClick={() => setStatus(visit.id, 'in_progress')}
                                className='flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold transition-colors'>
                                <IoPlayCircleOutline className='text-base' />Commencer
                              </button>
                            )}
                            {status === 'in_progress' && (
                              <>
                                <button onClick={() => setShowSignature(visit.id)}
                                  className='flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white text-sm font-semibold transition-all shadow-sm'>
                                  <IoPencilOutline className='text-base' />Signature patient
                                </button>
                                <button onClick={() => setStatus(visit.id, 'done')}
                                  className='flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors'>
                                  <IoCheckmarkCircle className='text-base' />Terminer sans signature
                                </button>
                                <button onClick={() => setStatus(visit.id, 'skipped')}
                                  className='flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-amber-600 text-sm font-medium transition-colors hover:bg-amber-100'>
                                  <IoAlertCircleOutline className='text-base' />Passer
                                </button>
                              </>
                            )}
                            {(status === 'done' || status === 'skipped') && (
                              <button onClick={() => setStatus(visit.id, 'pending')}
                                className='flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white dark:bg-dark-50 border border-gray-200 dark:border-gray-700 text-gray-500 text-sm font-medium hover:border-gray-400 transition-all'>
                                <IoRefreshOutline className='text-base' />Réouvrir
                              </button>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )
            })}
          </div>
        )}

        {/* ── Bouton ajouter ── */}
        {!isLoading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <button onClick={() => navigate('/add-appointment')}
              className='w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 hover:border-primary-400 hover:text-primary-500 transition-all font-medium text-sm'>
              <IoAddCircleOutline className='text-xl' />Ajouter une visite
            </button>
          </motion.div>
        )}

      </div>
    </div>
  )
}
